import React, { useState, useEffect } from 'react';
import { FileText, Type, Scissors, Hash, Search, Copy, RefreshCw, Check, X, BarChart3 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useLocation, Link as RouterLink, Routes, Route, useNavigate } from 'react-router-dom';

export const tabs = [
  { id: 'counter', name: 'Contador de Texto', icon: BarChart3, path: '/content/counter', description: 'Estatísticas detalhadas', status: 'active' },
  { id: 'formatter', name: 'Formatador de Texto', icon: Type, path: '/content/formatter', description: '16 tipos de formatação', status: 'active' },
  { id: 'lorem', name: 'Gerador Lorem Ipsum', icon: FileText, path: '/content/lorem', description: 'Texto placeholder', status: 'active' },
  { id: 'html-remover', name: 'Removedor de HTML', icon: Scissors, path: '/content/html-remover', description: 'Remove tags HTML', status: 'active' },
  { id: 'slug', name: 'Gerador de Slug', icon: Hash, path: '/content/slug', description: 'Slug para URLs', status: 'active' },
  { id: 'metadata', name: 'Extrator de Metadados', icon: Search, path: '/content/metadata', description: 'Estatísticas e metadados', status: 'active' },
  { id: 'comparator', name: 'Comparador de Textos', icon: FileText, path: '/content/comparator', description: 'Comparação de textos', status: 'active' }
];

const Content = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Detectar aba ativa pela URL
  const getActiveTab = () => {
    const match = tabs.find(tab => location.pathname.startsWith(tab.path));
    return match ? match.id : 'counter';
  };
  const activeTab = getActiveTab();

  // Redirecionar /content para /content/formatter
  useEffect(() => {
    if (location.pathname === '/content') {
      navigate('/content/formatter');
    }
  }, [location.pathname, navigate]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto w-full overflow-x-hidden bg-white min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Ferramentas de Conteúdo
        </h1>
        <p className="text-gray-600">
          Formatadores e analisadores de texto
        </p>
      </div>

      {/* Navegação rápida das ferramentas */}
      <div className="mb-8">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Ferramentas Disponíveis</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {tabs.filter(tab => tab.status === 'active').map(tab => (
            <RouterLink
              key={tab.id}
              to={tab.path}
              className={`p-4 bg-white border rounded-lg transition-all duration-200 flex items-center space-x-3 ${location.pathname === tab.path ? 'border-blue-500 bg-blue-50 shadow' : 'border-gray-200 hover:shadow-md hover:border-blue-300'}`}
            >
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <tab.icon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 text-base md:text-[15px] lg:text-base leading-tight break-words whitespace-normal">{tab.name}</h3>
                <p className="text-sm text-gray-500 leading-snug break-words whitespace-normal">{tab.description}</p>
              </div>
            </RouterLink>
          ))}
        </div>
      </div>

      <Routes>
        <Route path="counter" element={<TextCounter />} />
        <Route path="formatter" element={<TextFormatter />} />
        <Route path="lorem" element={<LoremIpsumGenerator />} />
        <Route path="html-remover" element={<HTMLRemover />} />
        <Route path="slug" element={<SlugGenerator />} />
        <Route path="metadata" element={<MetadataExtractor />} />
        <Route path="comparator" element={<TextComparator />} />
      </Routes>
    </div>
  );
};

// Componente Contador de Texto
const TextCounter = () => {
  const [text, setText] = useState('');

  const stats = {
    characters: text.length,
    charactersNoSpaces: text.replace(/\s/g, '').length,
    words: text.trim() ? text.trim().split(/\s+/).length : 0,
    lines: text ? text.split('\n').length : 0,
    paragraphs: text ? text.split('\n\n').filter(p => p.trim()).length : 0,
    sentences: text ? text.split(/[.!?]+/).filter(s => s.trim()).length : 0
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Texto para Analisar
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Digite ou cole o texto aqui..."
            className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        </div>

        {text && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-full">
            {Object.entries(stats).map(([key, value]) => (
              <div key={key} className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">{value}</div>
                <div className="text-sm text-gray-600 capitalize">
                  {key === 'characters' && 'Caracteres'}
                  {key === 'charactersNoSpaces' && 'Caracteres (sem espaços)'}
                  {key === 'words' && 'Palavras'}
                  {key === 'lines' && 'Linhas'}
                  {key === 'paragraphs' && 'Parágrafos'}
                  {key === 'sentences' && 'Frases'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Formatador de Texto
const TextFormatter = () => {
  const [text, setText] = useState('');
  const [formattedText, setFormattedText] = useState('');

  const formatText = (type) => {
    switch (type) {
      case 'uppercase':
        setFormattedText(text.toUpperCase());
        break;
      case 'lowercase':
        setFormattedText(text.toLowerCase());
        break;
      case 'capitalize':
        setFormattedText(text.replace(/\b\w/g, l => l.toUpperCase()));
        break;
      case 'titlecase':
        setFormattedText(text.replace(/\b\w+/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()));
        break;
      case 'removeSpaces':
        setFormattedText(text.replace(/\s+/g, ' ').trim());
        break;
      case 'removeLineBreaks':
        setFormattedText(text.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim());
        break;
      default:
        setFormattedText(text);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Texto para Formatar
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Digite o texto que deseja formatar..."
            className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <button onClick={() => formatText('uppercase')} className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            MAIÚSCULAS
          </button>
          <button onClick={() => formatText('lowercase')} className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            minúsculas
          </button>
          <button onClick={() => formatText('capitalize')} className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            Primeira Letra
          </button>
          <button onClick={() => formatText('titlecase')} className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Título
          </button>
          <button onClick={() => formatText('removeSpaces')} className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            Remover Espaços
          </button>
          <button onClick={() => formatText('removeLineBreaks')} className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            Remover Quebras
          </button>
        </div>

        {formattedText && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">Texto Formatado</span>
              <button onClick={() => copyToClipboard(formattedText)} className="p-1 text-gray-500 hover:text-gray-700">
                <Copy size={16} />
              </button>
            </div>
            <textarea
              value={formattedText}
              readOnly
              className="w-full h-32 p-3 border border-gray-300 rounded-lg bg-white text-gray-900 resize-none"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Removedor de HTML
const HTMLRemover = () => {
  const [htmlText, setHtmlText] = useState('');
  const [cleanText, setCleanText] = useState('');

  const removeHTML = () => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlText;
    setCleanText(tempDiv.textContent || tempDiv.innerText || '');
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Texto com HTML
          </label>
          <textarea
            value={htmlText}
            onChange={(e) => setHtmlText(e.target.value)}
            placeholder="Cole aqui o texto com tags HTML..."
            className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        </div>

        <button
          onClick={removeHTML}
          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Remover HTML
        </button>

        {cleanText && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">Texto Limpo</span>
              <button onClick={() => copyToClipboard(cleanText)} className="p-1 text-gray-500 hover:text-gray-700">
                <Copy size={16} />
              </button>
            </div>
            <textarea
              value={cleanText}
              readOnly
              className="w-full h-32 p-3 border border-gray-300 rounded-lg bg-white text-gray-900 resize-none"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Gerador de Slug
const SlugGenerator = () => {
  const [text, setText] = useState('');
  const [slug, setSlug] = useState('');

  const generateSlug = () => {
    const generated = text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
    setSlug(generated);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Texto para Slug
          </label>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Digite o texto para gerar o slug..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        </div>

        <button
          onClick={generateSlug}
          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Gerar Slug
        </button>

        {slug && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">Slug Gerado</span>
              <button onClick={() => copyToClipboard(slug)} className="p-1 text-gray-500 hover:text-gray-700">
                <Copy size={16} />
              </button>
            </div>
            <code className="text-sm font-mono text-gray-900 break-all">
              {slug}
            </code>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Extrator de Metadados
const MetadataExtractor = () => {
  const [text, setText] = useState('');
  const [metadata, setMetadata] = useState(null);

  const extractMetadata = () => {
    const stats = {
      characters: text.length,
      charactersNoSpaces: text.replace(/\s/g, '').length,
      words: text.trim() ? text.trim().split(/\s+/).length : 0,
      lines: text ? text.split('\n').length : 0,
      paragraphs: text ? text.split('\n\n').filter(p => p.trim()).length : 0,
      sentences: text ? text.split(/[.!?]+/).filter(s => s.trim()).length : 0,
      readingTime: Math.ceil(text.trim().split(/\s+/).length / 200), // 200 palavras por minuto
      speakingTime: Math.ceil(text.trim().split(/\s+/).length / 150), // 150 palavras por minuto
      uniqueWords: text.trim() ? new Set(text.toLowerCase().match(/\b\w+\b/g)).size : 0,
      averageWordLength: text.trim() ? (text.replace(/\s/g, '').length / text.trim().split(/\s+/).length).toFixed(1) : 0
    };
    setMetadata(stats);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Texto para Análise
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Digite ou cole o texto para extrair metadados..."
            className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        </div>

        <button
          onClick={extractMetadata}
          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Extrair Metadados
        </button>

        {metadata && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-full">
            {Object.entries(metadata).map(([key, value]) => (
              <div key={key} className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">{value}</div>
                <div className="text-sm text-gray-600 capitalize">
                  {key === 'characters' && 'Caracteres'}
                  {key === 'charactersNoSpaces' && 'Caracteres (sem espaços)'}
                  {key === 'words' && 'Palavras'}
                  {key === 'lines' && 'Linhas'}
                  {key === 'paragraphs' && 'Parágrafos'}
                  {key === 'sentences' && 'Frases'}
                  {key === 'readingTime' && 'Tempo de Leitura (min)'}
                  {key === 'speakingTime' && 'Tempo de Fala (min)'}
                  {key === 'uniqueWords' && 'Palavras Únicas'}
                  {key === 'averageWordLength' && 'Tamanho Médio das Palavras'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Comparador de Textos
const TextComparator = () => {
  const [text1, setText1] = useState('');
  const [text2, setText2] = useState('');
  const [comparison, setComparison] = useState(null);

  const compareTexts = () => {
    if (!text1.trim() || !text2.trim()) {
      toast.error('Digite ambos os textos para comparar');
      return;
    }

    const similarity = calculateSimilarity(text1, text2);
    const differences = findDifferences(text1, text2);

    setComparison({
      similarity: Math.round(similarity * 100),
      differences,
      text1Words: text1.trim().split(/\s+/).length,
      text2Words: text2.trim().split(/\s+/).length,
      commonWords: findCommonWords(text1, text2)
    });
  };

  const calculateSimilarity = (str1, str2) => {
    const words1 = new Set(str1.toLowerCase().match(/\b\w+\b/g) || []);
    const words2 = new Set(str2.toLowerCase().match(/\b\w+\b/g) || []);
    
    const intersection = new Set([...words1].filter(x => words2.has(x)));
    const union = new Set([...words1, ...words2]);
    
    return union.size > 0 ? intersection.size / union.size : 0;
  };

  const findDifferences = (str1, str2) => {
    const words1 = str1.toLowerCase().match(/\b\w+\b/g) || [];
    const words2 = str2.toLowerCase().match(/\b\w+\b/g) || [];
    
    const onlyInText1 = words1.filter(word => !words2.includes(word));
    const onlyInText2 = words2.filter(word => !words1.includes(word));
    
    return { onlyInText1: [...new Set(onlyInText1)], onlyInText2: [...new Set(onlyInText2)] };
  };

  const findCommonWords = (str1, str2) => {
    const words1 = new Set(str1.toLowerCase().match(/\b\w+\b/g) || []);
    const words2 = new Set(str2.toLowerCase().match(/\b\w+\b/g) || []);
    
    return [...words1].filter(word => words2.has(word));
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Texto 1
            </label>
            <textarea
              value={text1}
              onChange={(e) => setText1(e.target.value)}
              placeholder="Digite o primeiro texto..."
              className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Texto 2
            </label>
            <textarea
              value={text2}
              onChange={(e) => setText2(e.target.value)}
              placeholder="Digite o segundo texto..."
              className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>
        </div>

        <button
          onClick={compareTexts}
          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Comparar Textos
        </button>

        {comparison && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {comparison.similarity}%
              </div>
              <div className="text-sm text-gray-600">Similaridade</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">
                  {comparison.text1Words}
                </div>
                <div className="text-sm text-gray-600">Palavras no Texto 1</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">
                  {comparison.text2Words}
                </div>
                <div className="text-sm text-gray-600">Palavras no Texto 2</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {comparison.commonWords.length}
                </div>
                <div className="text-sm text-gray-600">Palavras Comuns</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Apenas no Texto 1</h4>
                <div className="text-sm text-gray-600">
                  {comparison.differences.onlyInText1.length > 0 
                    ? comparison.differences.onlyInText1.join(', ')
                    : 'Nenhuma palavra única'
                  }
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Apenas no Texto 2</h4>
                <div className="text-sm text-gray-600">
                  {comparison.differences.onlyInText2.length > 0 
                    ? comparison.differences.onlyInText2.join(', ')
                    : 'Nenhuma palavra única'
                  }
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Gerador Lorem Ipsum
const LoremIpsumGenerator = () => {
  const [paragraphs, setParagraphs] = useState(3);
  const [result, setResult] = useState('');

  const generateLorem = () => {
    const lorem = `Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed non risus. Suspendisse lectus tortor, dignissim sit amet, adipiscing nec, ultricies sed, dolor. Cras elementum ultrices diam. Maecenas ligula massa, varius a, semper congue, euismod non, mi.`;
    setResult(Array.from({ length: paragraphs }, () => lorem).join('\n\n'));
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Quantidade de Parágrafos
          </label>
          <input
            type="number"
            min={1}
            max={10}
            value={paragraphs}
            onChange={e => setParagraphs(Number(e.target.value))}
            className="w-24 p-2 border border-gray-300 rounded-lg mr-4"
          />
          <button
            onClick={generateLorem}
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Gerar Lorem Ipsum
          </button>
        </div>
        {result && (
          <textarea
            className="w-full h-40 p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 resize-none"
            value={result}
            readOnly
          />
        )}
      </div>
    </div>
  );
};

export default Content; 